// Schema validation utilities for SEO compliance

/**
 * Validates a JobPosting schema object against Google's requirements
 * @param {Object} schema - The schema object to validate
 * @returns {Object} - Validation result with errors and warnings
 */
export const validateJobPostingSchema = (schema) => {
  const errors = [];
  const warnings = [];

  // Required fields validation
  if (!schema.hiringOrganization?.name) {
    errors.push('Missing required field: hiringOrganization.name');
  }

  if (!schema.jobLocation?.address?.addressLocality) {
    errors.push('Missing required field: jobLocation.address.addressLocality');
  }

  if (!schema.jobLocation?.address?.addressCountry) {
    errors.push('Missing required field: jobLocation.address.addressCountry');
  }

  if (!schema.jobLocation?.address?.streetAddress) {
    errors.push('Missing required field: jobLocation.address.streetAddress');
  }

  if (!schema.jobLocation?.address?.addressRegion) {
    errors.push('Missing required field: jobLocation.address.addressRegion');
  }

  if (!schema.jobLocation?.address?.postalCode) {
    errors.push('Missing required field: jobLocation.address.postalCode');
  }

  if (!schema.experienceRequirements?.monthsOfExperience) {
    errors.push('Missing required field: experienceRequirements.monthsOfExperience');
  }

  if (!schema.educationRequirements?.credentialCategory) {
    errors.push('Missing required field: educationRequirements.credentialCategory');
  }

  // Warning checks
  if (!schema.title || schema.title === 'Job Opportunity') {
    warnings.push('Generic job title detected - consider using more specific titles');
  }

  if (!schema.description || schema.description.length < 50) {
    warnings.push('Job description is too short - aim for at least 50 characters');
  }

  if (!schema.datePosted) {
    warnings.push('Missing datePosted field');
  }

  if (!schema.validThrough) {
    warnings.push('Missing validThrough field');
  }

  if (!schema.employmentType) {
    warnings.push('Missing employmentType field');
  }

  if (!schema.baseSalary) {
    warnings.push('Missing baseSalary field');
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    schemaType: schema['@type'],
    context: schema['@context']
  };
};

/**
 * Logs schema validation results to console
 * @param {Object} validationResult - Result from validateJobPostingSchema
 * @param {string} jobTitle - Job title for context
 */
export const logSchemaValidation = (validationResult, jobTitle = 'Unknown Job') => {
  console.group(`🔍 Schema Validation for: ${jobTitle}`);
  
  if (validationResult.isValid) {
    console.log('✅ Schema is valid and ready for Google indexing');
  } else {
    console.error('❌ Schema has validation errors:');
    validationResult.errors.forEach(error => {
      console.error(`   - ${error}`);
    });
  }

  if (validationResult.warnings.length > 0) {
    console.warn('⚠️ Schema warnings:');
    validationResult.warnings.forEach(warning => {
      console.warn(`   - ${warning}`);
    });
  }

  console.log('📋 Schema Type:', validationResult.schemaType);
  console.log('🌐 Context:', validationResult.context);
  console.groupEnd();
};

/**
 * Formats experience string to months for schema compliance
 * @param {string} experience - Experience string (e.g., "5+ years", "2 years")
 * @returns {number} - Months of experience
 */
export const formatExperienceToMonths = (experience) => {
  if (!experience) return 12; // Default 1 year
  
  const experienceStr = experience.toString().toLowerCase();
  
  // Extract years from common formats
  const yearMatch = experienceStr.match(/(\d+)\s*year/);
  if (yearMatch) {
    return parseInt(yearMatch[1]) * 12;
  }
  
  // Extract months
  const monthMatch = experienceStr.match(/(\d+)\s*month/);
  if (monthMatch) {
    return parseInt(monthMatch[1]);
  }
  
  // Default fallback
  return 12;
};

/**
 * Formats qualification to credential category for schema compliance
 * @param {string} qualification - Qualification string
 * @returns {string} - Credential category
 */
export const formatQualificationToCredential = (qualification) => {
  if (!qualification) return 'Bachelor Degree';
  
  const qualStr = qualification.toString().toLowerCase();
  
  if (qualStr.includes('phd') || qualStr.includes('doctorate')) {
    return 'Doctorate Degree';
  } else if (qualStr.includes('master')) {
    return 'Master Degree';
  } else if (qualStr.includes('bachelor') || qualStr.includes('bachelor')) {
    return 'Bachelor Degree';
  } else if (qualStr.includes('associate') || qualStr.includes('diploma')) {
    return 'Associate Degree';
  } else if (qualStr.includes('high school') || qualStr.includes('secondary')) {
    return 'High School Diploma';
  } else if (qualStr.includes('certificate') || qualStr.includes('certification')) {
    return 'Professional Certificate';
  }
  
  return 'Bachelor Degree'; // Default fallback
};

/**
 * Parses location object to complete address structure
 * @param {Object} location - Location object from job data
 * @returns {Object} - Complete PostalAddress object
 */
export const parseLocationToAddress = (location) => {
  if (!location || typeof location !== 'object') {
    // Static fallback address for schema compliance
    return {
      '@type': 'PostalAddress',
      streetAddress: '123 Business Street',
      addressLocality: 'New York',
      addressRegion: 'NY',
      postalCode: '10001',
      addressCountry: 'US'
    };
  }

  // Try to parse address string if it's a string
  let addressParts = {
    streetAddress: '',
    addressLocality: '',
    addressRegion: '',
    postalCode: '',
    addressCountry: 'US' // Default to US
  };

  if (location.address && typeof location.address === 'string') {
    // Parse address string like "New York, NY" or "San Francisco, CA"
    const addressStr = location.address.replace(/\s*\(Remote\)/i, ''); // Remove remote indicator
    const parts = addressStr.split(',').map(part => part.trim());
    
    if (parts.length >= 2) {
      addressParts.addressLocality = parts[0];
      addressParts.addressRegion = parts[1];
    } else if (parts.length === 1) {
      addressParts.addressLocality = parts[0];
    }
  }

  // Use provided fields or fallbacks
  return {
    '@type': 'PostalAddress',
    streetAddress: location.street || addressParts.streetAddress || '123 Business Street',
    addressLocality: location.city || addressParts.addressLocality || 'New York',
    addressRegion: location.state || addressParts.addressRegion || 'NY',
    postalCode: location.postalCode || addressParts.postalCode || '10001',
    addressCountry: location.country || addressParts.addressCountry || 'US'
  };
}; 